import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ref, set, onValue, DatabaseReference } from 'firebase/database';
import { database } from '../../gen2ai/fire-init';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { CalendarConversationHistoryService } from '../../gen2ai/chats-calendar.service';
import { Conversation, Message } from '../../models/chat.model';

interface DayCell {
  date: Date;
  label: number;
  inCurrentMonth: boolean;
  isToday: boolean;
  hasNotes: boolean;
  notesCount: number;
  conversationCount: number;
}

interface DayNote {
  id: string;
  text: string;
  createdAt: string; // ISO string
  conversationId?: string;
  conversationTitle?: string;
}

type ViewMode = 'month' | 'week';

@Component({
  selector: 'app-calendar-notes',
  templateUrl: './calendar-notes.component.html',
  styleUrls: ['./calendar-notes.component.scss']
})
export class CalendarNotesComponent implements OnInit, OnDestroy {
  viewMode: ViewMode = 'month';
  today = new Date();
  currentAnchorDate = this.stripTime(new Date()); // center date for current view
  selectedDate = this.stripTime(new Date());

  days: DayCell[] = [];

  newNoteText = '';
  newNoteConversationId = '';
  notesByDay: Record<string, DayNote[]> = {};

  private notesRef: DatabaseReference | null = null;
  private notesListenerUnsubscribe: (() => void) | null = null;
  private currentUserId: string | null = null;

  private conversationsSub?: Subscription;
  private allConversations: Conversation[] = [];
  private dayConversations = new Map<string, Conversation[]>();

  constructor(
    private historyService: CalendarConversationHistoryService
  ) {}

  ngOnInit(): void {
    this.initUserContext();
    this.conversationsSub = this.historyService.conversations$.subscribe((convs) => {
      this.allConversations = convs || [];
      this.recomputeDailyAggregation();
      this.rebuildCalendar();
      this.syncNoteConversationSelection();
    });
  }

  ngOnDestroy(): void {
    this.cleanupListener();
    this.conversationsSub?.unsubscribe();
  }

  private initUserContext(): void {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      const userId = user?.uid || this.getOrCreateAnonymousUid();
      if (this.currentUserId !== userId) {
        this.currentUserId = userId;
        this.setupNotesListener();
        this.recomputeDailyAggregation();
        this.rebuildCalendar();
      }
    });
  }

  private getOrCreateAnonymousUid(): string {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return 'anon-' + uuidv4();
      }
      const key = 'clarichat_calendar_anon_uid';
      let anon = window.localStorage.getItem(key);
      if (!anon) {
        anon = 'anon-' + uuidv4();
        window.localStorage.setItem(key, anon);
      }
      return anon;
    } catch {
      return 'anon-' + uuidv4();
    }
  }

  private setupNotesListener(): void {
    this.cleanupListener();

    if (!this.currentUserId) {
      this.notesByDay = {};
      this.rebuildCalendar();
      return;
    }

    try {
      this.notesRef = ref(database, `calendarNotes/${this.currentUserId}`);
      
      this.notesListenerUnsubscribe = onValue(
        this.notesRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val();
            this.notesByDay = data || {};
          } else {
            this.notesByDay = {};
          }
          this.rebuildCalendar();
        },
        (err) => {
          console.error('[CalendarNotes] listener error', err);
          this.notesByDay = {};
          this.rebuildCalendar();
        }
      );
    } catch (err) {
      console.error('[CalendarNotes] setupNotesListener error', err);
      this.notesByDay = {};
      this.rebuildCalendar();
    }
  }

  private cleanupListener(): void {
    if (this.notesListenerUnsubscribe) {
      this.notesListenerUnsubscribe();
      this.notesListenerUnsubscribe = null;
    }
    this.notesRef = null;
  }

  /* ------------------------------------------------------------------ */
  /*  View + navigation                                                  */
  /* ------------------------------------------------------------------ */

  setViewMode(mode: ViewMode): void {
    if (this.viewMode === mode) return;
    this.viewMode = mode;
    this.rebuildCalendar();
  }

  goPrev(): void {
    if (this.viewMode === 'month') {
      const d = new Date(this.currentAnchorDate);
      d.setMonth(d.getMonth() - 1);
      this.currentAnchorDate = this.stripTime(d);
    } else {
      const d = new Date(this.currentAnchorDate);
      d.setDate(d.getDate() - 7);
      this.currentAnchorDate = this.stripTime(d);
    }
    this.rebuildCalendar();
  }

  goNext(): void {
    if (this.viewMode === 'month') {
      const d = new Date(this.currentAnchorDate);
      d.setMonth(d.getMonth() + 1);
      this.currentAnchorDate = this.stripTime(d);
    } else {
      const d = new Date(this.currentAnchorDate);
      d.setDate(d.getDate() + 7);
      this.currentAnchorDate = this.stripTime(d);
    }
    this.rebuildCalendar();
  }

  goToday(): void {
    this.currentAnchorDate = this.stripTime(new Date());
    this.selectedDate = this.stripTime(new Date());
    this.rebuildCalendar();
  }

  get headerLabel(): string {
    const d = this.currentAnchorDate;
    const month = d.toLocaleString(undefined, { month: 'long' });
    const year = d.getFullYear();
    if (this.viewMode === 'month') {
      return `${month} ${year}`;
    } else {
      const weekRange = this.getWeekRange(this.currentAnchorDate);
      const startLabel = weekRange.start.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric'
      });
      const endLabel = weekRange.end.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year:
          weekRange.end.getFullYear() !== weekRange.start.getFullYear()
            ? 'numeric'
            : undefined
      });
      return `Week of ${startLabel} – ${endLabel}`;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Calendar building                                                  */
  /* ------------------------------------------------------------------ */

  private rebuildCalendar(): void {
    if (this.viewMode === 'month') {
      this.buildMonthView();
    } else {
      this.buildWeekView();
    }
  }

  private buildMonthView(): void {
    const anchor = this.currentAnchorDate;
    const year = anchor.getFullYear();
    const month = anchor.getMonth();

    const firstOfMonth = new Date(year, month, 1);
    const firstWeekday = firstOfMonth.getDay(); // 0 = Sun

    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Start from the Sunday before (or equal to) the first of the month
    const startDate = new Date(year, month, 1 - firstWeekday);

    const cells: DayCell[] = [];
    // Always render 6 rows x 7 cols = 42 cells for a full grid
    for (let i = 0; i < 42; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);

      const inCurrentMonth = d.getMonth() === month;
      const isToday = this.isSameDay(d, this.today);
      const dateKey = this.toDateKey(d);
      const notesCount = (this.notesByDay[dateKey] || []).length;
      const conversationCount = (this.dayConversations.get(dateKey) || []).length;

      cells.push({
        date: d,
        label: d.getDate(),
        inCurrentMonth,
        isToday,
        hasNotes: notesCount > 0,
        notesCount,
        conversationCount
      });
    }

    this.days = cells;
  }

  private buildWeekView(): void {
    const weekRange = this.getWeekRange(this.currentAnchorDate);
    const cells: DayCell[] = [];

    for (let i = 0; i < 7; i++) {
      const d = new Date(weekRange.start);
      d.setDate(weekRange.start.getDate() + i);

      const isToday = this.isSameDay(d, this.today);
      const dateKey = this.toDateKey(d);
      const notesCount = (this.notesByDay[dateKey] || []).length;
      const conversationCount = (this.dayConversations.get(dateKey) || []).length;

      cells.push({
        date: d,
        label: d.getDate(),
        inCurrentMonth: true,
        isToday,
        hasNotes: notesCount > 0,
        notesCount,
        conversationCount
      });
    }

    this.days = cells;
  }

  private getWeekRange(anchor: Date): { start: Date; end: Date } {
    // Week starts on Sunday
    const d = this.stripTime(anchor);
    const dayOfWeek = d.getDay(); // 0 = Sun
    const start = new Date(d);
    start.setDate(d.getDate() - dayOfWeek);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    return { start, end };
  }

  /* ------------------------------------------------------------------ */
  /*  Selection + notes                                                  */
  /* ------------------------------------------------------------------ */

  selectDay(day: DayCell): void {
    this.selectedDate = this.stripTime(day.date);
    this.syncNoteConversationSelection();
  }

  get selectedDateLabel(): string {
    return this.selectedDate.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  get selectedDayNotes(): DayNote[] {
    const key = this.toDateKey(this.selectedDate);
    return (this.notesByDay[key] || []).slice().sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt)
    );
  }

  get selectedDayConversations(): Conversation[] {
    const key = this.toDateKey(this.selectedDate);
    return (this.dayConversations.get(key) || []).slice().sort((a, b) => {
      const aTitle = (a.title || '').toLowerCase();
      const bTitle = (b.title || '').toLowerCase();
      return aTitle.localeCompare(bTitle);
    });
  }

  addNote(): void {
    const text = (this.newNoteText || '').trim();
    if (!text || !this.currentUserId) return;

    const key = this.toDateKey(this.selectedDate);
    const now = new Date();
    const conversationId = (this.newNoteConversationId || '').trim();
    const conversation = conversationId
      ? this.allConversations.find((conv) => conv.id === conversationId)
      : undefined;

    const note: DayNote = {
      id: `${key}-${now.getTime()}`,
      text,
      createdAt: now.toISOString(),
      conversationId: conversation?.id,
      conversationTitle: conversation?.title || undefined
    };

    if (!this.notesByDay[key]) {
      this.notesByDay[key] = [];
    }
    this.notesByDay[key].push(note);
    this.newNoteText = '';
    this.newNoteConversationId = '';

    this.saveToFirebase();
    // Note: rebuildCalendar will be called automatically by the real-time listener
  }

  deleteNote(note: DayNote): void {
    if (!this.currentUserId) return;

    const key = this.toDateKey(this.selectedDate);
    const list = this.notesByDay[key] || [];
    this.notesByDay[key] = list.filter((n) => n.id !== note.id);
    if (this.notesByDay[key].length === 0) {
      delete this.notesByDay[key];
    }
    this.saveToFirebase();
    // Note: rebuildCalendar will be called automatically by the real-time listener
  }

  /* ------------------------------------------------------------------ */
  /*  Firebase Storage                                                   */
  /* ------------------------------------------------------------------ */

  private async saveToFirebase(): Promise<void> {
    if (!this.currentUserId) return;

    try {
      const notesRef = ref(database, `calendarNotes/${this.currentUserId}`);
      await set(notesRef, this.notesByDay);
    } catch (err) {
      console.error('[CalendarNotes] saveToFirebase error', err);
    }
  }

  private recomputeDailyAggregation(): void {
    this.dayConversations.clear();

    for (const conv of this.allConversations) {
      if (this.currentUserId && conv.uid && conv.uid !== this.currentUserId) {
        continue;
      }

      const msgList = conv.messages || [];
      const datesSeen = new Set<string>();

      for (const msg of msgList as Message[]) {
        if (!msg || !msg.timestamp) continue;

        const d =
          msg.timestamp instanceof Date
            ? msg.timestamp
            : new Date(msg.timestamp as any);

        const key = this.toDateKey(d);
        if (datesSeen.has(key)) continue;
        datesSeen.add(key);

        const list = this.dayConversations.get(key) || [];
        list.push(conv);
        this.dayConversations.set(key, list);
      }

      if (msgList.length === 0 && conv.createdAt) {
        const d = conv.createdAt instanceof Date ? conv.createdAt : new Date(conv.createdAt as any);
        const key = this.toDateKey(d);
        const list = this.dayConversations.get(key) || [];
        list.push(conv);
        this.dayConversations.set(key, list);
      }
    }
  }

  private syncNoteConversationSelection(): void {
    if (!this.newNoteConversationId) return;
    const matches = this.selectedDayConversations.some(
      (conv) => conv.id === this.newNoteConversationId
    );
    if (!matches) {
      this.newNoteConversationId = '';
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */

  private stripTime(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  // <-- made public so template can call it
  isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private toDateKey(d: Date): string {
    return d.toISOString().slice(0, 10); // yyyy-MM-dd
  }

  truncateConversationTopic(topic: string): string {
    const value = String(topic || '').trim();
    if (!value) return 'Conversation';
    return value.length <= 13 ? value : `${value.slice(0, 13)}...`;
  }
}
